import { useState, useRef, useCallback, useEffect } from "react";

const API_BASE_URL = process.env.API_BASE_URL || "https://api-test.getrima.ai";

interface UseSpeechToTextOptions {
  onTranscript?: (text: string) => void;
  onFinalTranscript?: (text: string) => void;
  onError?: (error: string) => void;
}

export function useSpeechToText(options: UseSpeechToTextOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isReadyRef = useRef(false);
  const fullTranscriptRef = useRef("");

  // Use refs to always have the latest callbacks (prevents stale closures in WebSocket handlers)
  const onTranscriptRef = useRef(options.onTranscript);
  const onFinalTranscriptRef = useRef(options.onFinalTranscript);
  const onErrorRef = useRef(options.onError);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onTranscriptRef.current = options.onTranscript;
    onFinalTranscriptRef.current = options.onFinalTranscript;
    onErrorRef.current = options.onError;
  }, [options.onTranscript, options.onFinalTranscript, options.onError]);

  // Convert Float32Array to Int16Array (PCM16)
  const floatTo16BitPCM = (float32Array: Float32Array): Int16Array => {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
  };

  // Downsample audio from source sample rate to target sample rate (16kHz)
  const downsample = (
    buffer: Float32Array,
    sourceSampleRate: number,
    targetSampleRate: number
  ): Float32Array => {
    if (sourceSampleRate === targetSampleRate) {
      return buffer;
    }
    const ratio = sourceSampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const index = Math.round(i * ratio);
      result[i] = buffer[index];
    }
    return result;
  };

  const startAudioCapture = async (ws: WebSocket) => {
    try {
      // Get microphone access - request 16kHz mono audio
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      // Create AudioContext with target sample rate (16kHz for Azure Speech)
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);

      // Use ScriptProcessorNode for audio processing
      // Buffer size of 4096 samples at 16kHz = ~256ms chunks
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (ws.readyState !== WebSocket.OPEN || !isReadyRef.current) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);

        // Downsample if needed (browser might not honor our sampleRate request)
        const targetSampleRate = 16000;
        const downsampled = downsample(inputData, audioContext.sampleRate, targetSampleRate);

        // Convert to PCM16
        const pcm16 = floatTo16BitPCM(downsampled);

        // Send as binary frame (recommended)
        ws.send(pcm16.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);
      console.log("Audio capture started, sample rate:", audioContext.sampleRate);
    } catch (err: any) {
      console.error("Failed to start audio capture:", err);
      const errorMessage = err.message || "Failed to access microphone";
      setError(errorMessage);
      onErrorRef.current?.(errorMessage);
      stopRecording();
    }
  };

  const stopRecording = useCallback(() => {
    console.log("Stopping recording...");

    // Stop processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsRecording(false);
    isReadyRef.current = false;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript("");
      fullTranscriptRef.current = "";
      isReadyRef.current = false;

      // Get token from localStorage (Excel plugin auth)
      const token = localStorage.getItem("rima_token");
      if (!token) {
        throw new Error("Unable to get authentication token. Please log in first.");
      }

      // Construct WebSocket URL based on API_BASE_URL
      const wsBaseUrl = API_BASE_URL.replace("https://", "wss://").replace("http://", "ws://");
      const wsUrl = `${wsBaseUrl}/api/v1/speech/transcribe/stream?token=${token}`;

      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("Speech WebSocket connected, waiting for ready signal...");
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WebSocket message:", data.type);

          // Handle ready signal from server
          if (data.type === "ready") {
            console.log("Server ready, starting audio capture");
            isReadyRef.current = true;
            await startAudioCapture(ws);
            return;
          }

          // Handle partial/incremental transcript
          if (data.type === "transcription.partial") {
            const partialText = data.text || "";
            setTranscript(partialText);
            // Use ref to call latest callback
            onTranscriptRef.current?.(partialText);
          }

          // Handle final transcript
          if (data.type === "transcription.final") {
            const finalText = data.text || "";
            fullTranscriptRef.current += finalText + " ";
            setTranscript(fullTranscriptRef.current.trim());
            // Use ref to call latest callback
            onFinalTranscriptRef.current?.(finalText);
          }

          // Handle session stopped
          if (data.type === "session.stopped") {
            console.log("Azure session stopped");
          }

          // Handle errors
          if (data.type === "error") {
            const errorMsg = data.data?.error || data.error || "Transcription error";
            console.error("Server error:", errorMsg);
            setError(errorMsg);
            // Use ref to call latest callback
            onErrorRef.current?.(errorMsg);
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };

      ws.onerror = (event) => {
        console.error("Speech WebSocket error:", event);
        setError("Connection error occurred");
        onErrorRef.current?.("Connection error occurred");
        stopRecording();
      };

      ws.onclose = (event) => {
        console.log("Speech WebSocket closed:", event.code, event.reason);
        setIsRecording(false);
        isReadyRef.current = false;
      };
    } catch (err: any) {
      console.error("Failed to start recording:", err);
      const errorMessage = err.message || "Failed to start recording";
      setError(errorMessage);
      onErrorRef.current?.(errorMessage);
      setIsRecording(false);
    }
  }, [stopRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    transcript,
    error,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
